import { useMutation, useQueryClient } from '@tanstack/react-query';

import { track } from '@/shared/analytics';
import {
  archiveBrandMedia,
  createBrandMedia,
  ingestBrandMedia,
  removeTagValue,
  renameTagValue,
  setMediaTags,
  type BrandMediaItem,
  type CreateBrandMediaInput,
} from './brandMedia.api';
import { BRAND_MEDIA_QUERY_KEYS } from './queries';

/**
 * Records an uploaded asset in the library and invalidates every question about it.
 *
 * Invalidated by prefix rather than by the exact listing: an upload changes what a search answers,
 * what a filtered gallery answers and what the bound lookup answers, and a caller that had to name
 * each of those would eventually miss one and leave a picture that exists but does not appear.
 *
 * The row is usable straight away. Ingestion measures and catalogues it behind this, so what comes
 * back has a title and pixels and not yet a description; the listing fills in on a later fetch.
 */
export function useCreateBrandMedia() {
  const queryClient = useQueryClient();

  return useMutation<BrandMediaItem, Error, CreateBrandMediaInput>({
    mutationFn: createBrandMedia,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: BRAND_MEDIA_QUERY_KEYS.all }),
  });
}

export interface SetMediaTagsInput {
  mediaId: string;
  tags: Record<string, string[]>;
}

/**
 * Sets one facet's values on a picture and invalidates every question about it.
 *
 * Invalidated by the same broad prefix `useCreateBrandMedia` already uses: an edit changes both
 * the listing (the row's own `tags`) and the facets query the tag rail and this editor's own
 * suggestions read, so a narrower invalidation would eventually miss one of them. Neither query
 * carries a `staleTime`, so the refetch this triggers shows the person their own edit immediately.
 */
export function useSetMediaTags() {
  const queryClient = useQueryClient();

  return useMutation<BrandMediaItem, Error, SetMediaTagsInput>({
    mutationFn: ({ mediaId, tags }) => setMediaTags(mediaId, tags),
    onSuccess: (_updated, { mediaId, tags }) => {
      void queryClient.invalidateQueries({ queryKey: BRAND_MEDIA_QUERY_KEYS.all });
      const [facetKey, values] = Object.entries(tags)[0] ?? ['', []];
      track('brand_media_tags_updated', {
        media_id: mediaId,
        facet_key: facetKey,
        value_count: values.length,
      });
    },
  });
}

export interface RenameTagValueInput {
  key: string;
  from: string;
  to: string;
  /** Whether the target already existed, so the two were folded into one. Reported, never decided here. */
  merged: boolean;
}

/**
 * Renames a tag value across the library, invalidating every question about it.
 *
 * The same broad prefix its neighbours use, and here it earns it twice over: this changes the tags
 * on rows the client never fetched, so there is no narrower key that could name what moved.
 *
 * No toast. Whether this reads as a rename or a merge is the calling surface's to know, the same
 * split `useSetMediaTags` already leaves to its editor.
 */
export function useRenameTagValue() {
  const queryClient = useQueryClient();

  return useMutation<{ updated: number }, Error, RenameTagValueInput>({
    mutationFn: ({ key, from, to }) => renameTagValue(key, from, to),
    onSuccess: (result, { merged }) => {
      void queryClient.invalidateQueries({ queryKey: BRAND_MEDIA_QUERY_KEYS.all });
      track('brand_media_tag_renamed', { merged, affected: result.updated });
    },
  });
}

export interface RemoveTagValueInput {
  key: string;
  value: string;
}

/** Takes a tag value off the whole library. Same invalidation and the same silence as the rename. */
export function useRemoveTagValue() {
  const queryClient = useQueryClient();

  return useMutation<{ updated: number }, Error, RemoveTagValueInput>({
    mutationFn: ({ key, value }) => removeTagValue(key, value),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: BRAND_MEDIA_QUERY_KEYS.all });
      track('brand_media_tag_removed', { affected: result.updated });
    },
  });
}

export interface DeleteBrandMediaInput {
  mediaIds: readonly string[];
  /** Which control started it. The same call either way, but two different journeys to read back. */
  surface: 'bulk_bar' | 'detail_panel';
}

/**
 * Deletes media and invalidates every question about it.
 *
 * The same broad prefix the other two mutations use: a delete changes the listing, the facet counts
 * the tag rail reads, the folder tree's counts and the bound lookup a board resolves through, and
 * naming those separately is how one gets missed and a folder shows a count that opening it cannot
 * produce.
 *
 * No toasts here. The wording depends on whether one asset or several were deleted, which is the
 * calling surface's to know -- the same split `useSetMediaTags` already leaves to its editor.
 */
export function useDeleteBrandMedia() {
  const queryClient = useQueryClient();

  return useMutation<{ archived: number }, Error, DeleteBrandMediaInput>({
    mutationFn: ({ mediaIds }) => archiveBrandMedia(mediaIds),
    onSuccess: (_result, { mediaIds, surface }) => {
      void queryClient.invalidateQueries({ queryKey: BRAND_MEDIA_QUERY_KEYS.all });
      track('brand_media_deleted', { count: mediaIds.length, surface });
    },
  });
}

/**
 * Re-runs ingestion over everything in the workspace it has not finished with.
 *
 * The one retry path the library has: a description that a model refused (a vector it cannot read, a
 * value outside a closed vocabulary, a rate limit) leaves a row that looks identical to one still
 * being catalogued, and nothing else in the product asks again.
 *
 * Invalidated by the same broad prefix as its neighbours, though the tags will not have landed by
 * the time this resolves -- the endpoint answers once the work is queued. The refetch that shows them
 * is the listing's own ingestion poll; this invalidation only makes sure the poll starts from a fresh
 * answer rather than a cached one.
 */
export function useIngestBrandMedia() {
  const queryClient = useQueryClient();

  return useMutation<{ started: number }, Error, { mediaId: string }>({
    mutationFn: () => ingestBrandMedia(),
    onSuccess: (_result, { mediaId }) => {
      void queryClient.invalidateQueries({ queryKey: BRAND_MEDIA_QUERY_KEYS.all });
      track('brand_media_tagging_retried', { media_id: mediaId });
    },
  });
}
